import {
  annualLeaveWeeksOf,
  annualRequestExceedsAccrual,
  assertAnnualAllowed,
  assertSickAllowed,
  assertStudyAllowed,
  contractualAnnualDays,
  dayEquivalentsPerWeek,
  canCancelRequest,
  expandLeaveDays,
  leaveBalance,
  leaveDaysOverlap,
  workingPatternFromInput,
} from '../modules/leave/accrual'
import { monthBounds } from '../modules/leave/dates'
import { payPeriodForPayday, reportDueDate, upcomingPayday } from '../modules/leave/payCycle'
import { approvalEmails, buildAdminReportEmail, buildBalanceEmail, buildDecisionEmail, buildPayReportLines, buildReportReadyEmail, buildRequestNoticeEmail, buildWelcomeEmail } from '../modules/leave/payReport'
import { generatePin, hashPin, isSixDigitPin } from '../modules/leave/pin'
import { isLeaveModuleEnabled, leaveToday, readLeaveModuleDevOverride } from '../modules/leave/productModule'
import type {
  AdditionalHours,
  DayPortion,
  LeaveBalance,
  LeaveCalendar,
  LeaveEmail,
  LeaveEmployee,
  LeaveRequest,
  LeaveSettings,
  LeaveType,
  PayReport,
} from '../modules/leave/types'

interface StoredEmployee extends LeaveEmployee {
  pinHash: string
}

interface StoredRequest extends LeaveRequest {}

interface StoredReport {
  payday: string
  dueDate: string
  periodStart: string
  periodEnd: string
  status: 'pending_approval' | 'sent'
  lines: PayReport['lines']
  additionalHours: AdditionalHours[]
  readyEmailSent: boolean
}

const SESSION_KEY = 'clnicl_leave_employee_id'

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(16).slice(2)}`
}

function employeeEmail(value: unknown): string {
  const email = String(value || '').trim()
  if (!email) return ''
  if (!email.includes('@')) throw new Error('Enter a valid email address, or leave it blank')
  return email
}

function assertPinAvailable(pin: string, ignoreId?: string): void {
  if (!isSixDigitPin(pin)) throw new Error('PIN must be 6 digits')
  const taken = employees.some((item) => item.id !== ignoreId && item.pinHash === hashPin(pin))
  if (taken) throw new Error('That PIN is already used by another employee')
}

function seedEmployee(input: Omit<StoredEmployee, 'pinHash'> & { pin: string }): StoredEmployee {
  const { pin, ...employee } = input
  return { ...employee, pinHash: hashPin(pin) }
}

function defaultEmployees(): StoredEmployee[] {
  return [
    seedEmployee({
      id: 'emp-rachel',
      name: 'Rachel',
      email: 'rachel@clinic.example',
      regularWeeklyHours: 39,
      workingDays: [1, 2, 3, 4, 5],
      hoursPerDay: 7.8,
      annualLeaveDays: 25,
      annualLeaveWeeks: 5,
      startDate: '2018-03-01',
      active: true,
      pin: '111111',
    }),
    seedEmployee({
      id: 'emp-jason',
      name: 'Jason',
      email: 'jason@clinic.example',
      regularWeeklyHours: 39,
      workingDays: [1, 2, 3, 4, 5],
      hoursPerDay: 7.8,
      annualLeaveDays: 25,
      annualLeaveWeeks: 5,
      startDate: '2019-06-01',
      active: true,
      pin: '222222',
    }),
    seedEmployee({
      id: 'emp-aoife',
      name: 'Aoife Kelly',
      email: 'aoife@clinic.example',
      regularWeeklyHours: 39,
      workingDays: [1, 2, 3, 4, 5],
      hoursPerDay: 7.8,
      annualLeaveDays: 20,
      annualLeaveWeeks: 4,
      startDate: '2022-01-10',
      active: true,
      pin: '333333',
    }),
    seedEmployee({
      id: 'emp-liam',
      name: 'Liam Byrne',
      email: 'liam@clinic.example',
      regularWeeklyHours: 20,
      workingDays: [1, 2, 3, 4, 5],
      hoursPerDay: 4,
      annualLeaveDays: 20,
      annualLeaveWeeks: 4,
      startDate: '2024-06-01',
      active: true,
      pin: '444444',
    }),
  ]
}

function defaultRequests(): StoredRequest[] {
  const createdAt = '2026-09-01T09:00:00.000Z'
  return [
    {
      id: 'lv-aoife-oct',
      employeeId: 'emp-aoife',
      employeeName: 'Aoife Kelly',
      type: 'annual',
      startDate: '2026-10-12',
      endDate: '2026-10-14',
      startPortion: 1,
      endPortion: 1,
      status: 'approved',
      createdAt,
    },
    {
      id: 'lv-liam-oct',
      employeeId: 'emp-liam',
      employeeName: 'Liam Byrne',
      type: 'sick',
      startDate: '2026-10-06',
      endDate: '2026-10-06',
      startPortion: 1,
      endPortion: 1,
      status: 'approved',
      createdAt,
    },
    {
      id: 'lv-rachel-oct',
      employeeId: 'emp-rachel',
      employeeName: 'Rachel',
      type: 'annual',
      startDate: '2026-10-20',
      endDate: '2026-10-20',
      startPortion: 0.5,
      endPortion: 0.5,
      status: 'approved',
      createdAt,
    },
    {
      id: 'lv-rachel-sick',
      employeeId: 'emp-rachel',
      employeeName: 'Rachel',
      type: 'sick',
      startDate: '2026-11-04',
      endDate: '2026-11-04',
      startPortion: 1,
      endPortion: 1,
      status: 'pending',
      createdAt: '2026-10-01T09:00:00.000Z',
    },
    {
      id: 'lv-jason-nov',
      employeeId: 'emp-jason',
      employeeName: 'Jason',
      type: 'annual',
      startDate: '2026-11-16',
      endDate: '2026-11-18',
      startPortion: 1,
      endPortion: 1,
      status: 'pending',
      note: 'Family visit',
      createdAt: '2026-10-01T10:00:00.000Z',
    },
    {
      id: 'lv-liam-nov',
      employeeId: 'emp-liam',
      employeeName: 'Liam Byrne',
      type: 'annual',
      startDate: '2026-11-23',
      endDate: '2026-11-23',
      startPortion: 0.5,
      endPortion: 0.5,
      status: 'pending',
      createdAt: '2026-10-02T09:00:00.000Z',
    },
    {
      id: 'lv-aoife-dec',
      employeeId: 'emp-aoife',
      employeeName: 'Aoife Kelly',
      type: 'annual',
      startDate: '2026-12-01',
      endDate: '2026-12-18',
      startPortion: 1,
      endPortion: 1,
      status: 'pending',
      createdAt: '2026-10-02T11:00:00.000Z',
    },
    {
      id: 'lv-rachel-dec',
      employeeId: 'emp-rachel',
      employeeName: 'Rachel',
      type: 'annual',
      startDate: '2026-12-07',
      endDate: '2026-12-08',
      startPortion: 1,
      endPortion: 1,
      status: 'approved',
      createdAt: '2026-09-15T09:00:00.000Z',
    },
    {
      id: 'lv-jason-dec',
      employeeId: 'emp-jason',
      employeeName: 'Jason',
      type: 'annual',
      startDate: '2026-12-14',
      endDate: '2026-12-16',
      startPortion: 1,
      endPortion: 1,
      status: 'approved',
      createdAt: '2026-09-15T10:00:00.000Z',
    },
  ]
}

export function sampleLeaveEmails(): LeaveEmail[] {
  const clinic: LeaveSettings = {
    accountantEmail: 'accountant@clinic.example',
    adminEmails: ['rachel@clinic.example', 'jason@clinic.example'],
    statutorySickDays: 5,
  }
  const septemberLines: PayReport['lines'] = [
    {
      employeeId: 'emp-aoife',
      employeeName: 'Aoife Kelly',
      type: 'annual',
      startDate: '2026-09-07',
      endDate: '2026-09-08',
      days: 2,
      hours: 15.6,
    },
    {
      employeeId: 'emp-liam',
      employeeName: 'Liam Byrne',
      type: 'sick',
      startDate: '2026-09-14',
      endDate: '2026-09-14',
      days: 1,
      hours: 4,
    },
  ]
  const septemberHours: AdditionalHours[] = [{
    id: 'hrs-seed-rachel',
    employeeId: 'emp-rachel',
    employeeName: 'Rachel',
    date: '2026-09-18',
    hours: 7.8,
    note: 'Reception cover',
    createdAt: '2026-09-18T16:00:00.000Z',
  }]
  const september = {
    payday: '2026-09-28',
    periodStart: '2026-08-29',
    periodEnd: '2026-09-28',
    lines: septemberLines,
    additionalHours: septemberHours,
    settings: clinic,
  }
  const items: Array<LeaveEmail | null> = [
    buildWelcomeEmail({
      name: 'Liam Byrne',
      email: 'liam@clinic.example',
      pin: '444444',
      createdAt: '2026-09-02T09:10:00.000Z',
    }),
    buildReportReadyEmail({
      ...september,
      dueDate: '2026-09-21',
      createdAt: '2026-09-21T08:00:00.000Z',
    }),
    ...approvalEmails({
      ...september,
      status: 'pending_approval',
      createdAt: '2026-09-22T11:30:00.000Z',
    }),
    buildRequestNoticeEmail({
      requestId: 'seed-jason-nov',
      action: 'submitted',
      name: 'Jason',
      type: 'annual',
      startDate: '2026-11-16',
      endDate: '2026-11-18',
      startPortion: 1,
      endPortion: 1,
      note: 'Family visit',
      settings: clinic,
      createdAt: '2026-10-01T10:05:00.000Z',
    }),
    buildBalanceEmail({
      name: 'Rachel',
      email: 'rachel@clinic.example',
      asOf: '2026-10-01',
      annualAccrued: 18.7,
      annualTaken: 0.5,
      annualBooked: 2,
      annualAvailable: 16.2,
      sickAvailable: 5,
      sickEntitlement: 5,
      studyAvailable: 0,
      studyEntitlement: 0,
      createdAt: '2026-10-01T15:20:00.000Z',
    }),
    buildDecisionEmail({
      requestId: 'seed-aoife-oct',
      name: 'Aoife Kelly',
      email: 'aoife@clinic.example',
      decision: 'approved',
      type: 'annual',
      startDate: '2026-10-12',
      endDate: '2026-10-14',
      startPortion: 1,
      endPortion: 1,
      asOf: '2026-10-02',
      annualAccrued: 15.1,
      annualTaken: 0,
      annualBooked: 3,
      annualAvailable: 12.1,
      sickEligible: true,
      sickEligibleFrom: '2022-04-11',
      sickAvailable: 5,
      sickEntitlement: 5,
      studyAvailable: 0,
      studyEntitlement: 0,
      studyYearStart: '2026-01-10',
      studyYearEnd: '2027-01-09',
      createdAt: '2026-10-02T09:40:00.000Z',
    }),
    buildRequestNoticeEmail({
      requestId: 'seed-rachel-cancel',
      action: 'cancelled',
      name: 'Rachel',
      type: 'sick',
      startDate: '2026-11-04',
      endDate: '2026-11-04',
      startPortion: 1,
      endPortion: 1,
      settings: clinic,
      createdAt: '2026-10-02T16:05:00.000Z',
    }),
    buildDecisionEmail({
      requestId: 'seed-liam-nov',
      name: 'Liam Byrne',
      email: 'liam@clinic.example',
      decision: 'declined',
      type: 'annual',
      startDate: '2026-11-23',
      endDate: '2026-11-23',
      startPortion: 0.5,
      endPortion: 0.5,
      decisionNote: 'Please take this after the clinic meeting.',
      asOf: '2026-10-03',
      annualAccrued: 8.4,
      annualTaken: 1,
      annualBooked: 0,
      annualAvailable: 7.4,
      sickEligible: true,
      sickEligibleFrom: '2024-08-30',
      sickAvailable: 4,
      sickEntitlement: 5,
      studyAvailable: 0,
      studyEntitlement: 0,
      studyYearStart: '2026-06-01',
      studyYearEnd: '2027-05-31',
      createdAt: '2026-10-03T14:15:00.000Z',
    }),
    buildAdminReportEmail({
      ...september,
      payday: '2026-10-28',
      periodStart: '2026-09-29',
      periodEnd: '2026-10-28',
      lines: [
        {
          employeeId: 'emp-aoife',
          employeeName: 'Aoife Kelly',
          type: 'annual',
          startDate: '2026-10-12',
          endDate: '2026-10-14',
          days: 3,
          hours: 23.4,
        },
      ],
      additionalHours: [],
      createdAt: '2026-10-03T16:45:00.000Z',
    }),
  ]
  return items.filter((item): item is LeaveEmail => item !== null)
}

let employees: StoredEmployee[] = defaultEmployees()
let requests: StoredRequest[] = defaultRequests()
let emails: LeaveEmail[] = sampleLeaveEmails()
let reports: StoredReport[] = []
let additionalHours: AdditionalHours[] = []
let settings: LeaveSettings = {
  accountantEmail: 'accountant@clinic.example',
  adminEmails: ['rachel@clinic.example', 'jason@clinic.example'],
  statutorySickDays: 5,
}

export function resetLeaveMockForTests(): void {
  employees = defaultEmployees()
  requests = defaultRequests()
  emails = []
  reports = []
  additionalHours = []
  settings = {
    accountantEmail: 'accountant@clinic.example',
    adminEmails: ['rachel@clinic.example', 'jason@clinic.example'],
    statutorySickDays: 5,
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_KEY)
  }
}

function byEmployeeName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
}

function roundLeaveDays(value: number): number {
  return Math.round(value * 100) / 100
}

function assignAnnualLeave(employee: StoredEmployee, payload: Record<string, unknown>, required: boolean): void {
  if (payload.annualLeaveWeeks !== undefined && payload.annualLeaveWeeks !== null && payload.annualLeaveWeeks !== '') {
    const weeks = Number(payload.annualLeaveWeeks)
    if (!Number.isFinite(weeks) || weeks < 0) throw new Error('Annual leave weeks cannot be negative')
    employee.annualLeaveWeeks = weeks
  } else if (payload.annualLeaveDays !== undefined && payload.annualLeaveDays !== null && payload.annualLeaveDays !== '') {
    const days = Number(payload.annualLeaveDays)
    if (!Number.isFinite(days) || days < 0) throw new Error('Annual leave days cannot be negative')
    const equivalents = dayEquivalentsPerWeek(employee)
    employee.annualLeaveWeeks = equivalents > 0 ? days / equivalents : 0
  } else if (typeof employee.annualLeaveWeeks !== 'number') {
    if (required) throw new Error('Annual leave weeks are required')
    const equivalents = dayEquivalentsPerWeek(employee)
    const days = Number(employee.annualLeaveDays)
    employee.annualLeaveWeeks = equivalents > 0 && Number.isFinite(days) ? days / equivalents : 4
  }
  employee.annualLeaveDays = roundLeaveDays(contractualAnnualDays(employee))
}

function publicEmployee(employee: StoredEmployee): LeaveEmployee {
  const { pinHash: _pinHash, ...rest } = employee
  return {
    ...rest,
    annualLeaveWeeks: annualLeaveWeeksOf(employee),
    annualLeaveDays: roundLeaveDays(contractualAnnualDays(employee)),
    studyLeaveDays: typeof employee.studyLeaveDays === 'number' ? employee.studyLeaveDays : 0,
  }
}

function studyLeaveDaysFrom(value: unknown): number {
  if (value === undefined || value === null || value === '') return 0
  const days = Number(value)
  if (!Number.isFinite(days) || days < 0) throw new Error('Study leave days cannot be negative')
  return days
}

function employeeWithBalance(employee: StoredEmployee, asOf: string): LeaveEmployee & { balance: LeaveBalance } {
  return {
    ...publicEmployee(employee),
    balance: leaveBalance(employee, requests, asOf, settings.statutorySickDays),
  }
}

function isLeaveMockEnabled(): boolean {
  let productType: 'pms' | 'ppt' = 'pms'
  let tenantEnabled = false
  if (typeof window !== 'undefined') {
    const override = window.localStorage.getItem('clnicl_dev_product_type_override')
    const storedProduct = override || window.localStorage.getItem('tenant_product_type')
    if (storedProduct === 'ppt') productType = 'ppt'
    const rawSettings = window.localStorage.getItem('clnicl_tenant_settings')
    if (rawSettings) {
      try {
        const parsed = JSON.parse(rawSettings) as { modules?: { leave?: boolean } }
        tenantEnabled = parsed.modules?.leave === true
      } catch {
        tenantEnabled = false
      }
    }
  }
  return isLeaveModuleEnabled({
    productType,
    tenantEnabled,
    devOverride: readLeaveModuleDevOverride(),
  })
}

function assertEnabled(): void {
  if (!isLeaveMockEnabled()) {
    throw new Error('Leave module is not enabled')
  }
}

function currentUserIsAdmin(): boolean {
  if (typeof window === 'undefined') return false
  const raw = window.localStorage.getItem('clnicl_user')
  if (!raw) return false
  try {
    const user = JSON.parse(raw) as { role?: string; permissions?: string[] }
    return user.role === 'admin' || user.role === 'system-admin' || !!user.permissions?.includes('*')
  } catch {
    return false
  }
}

function assertAdmin(): void {
  if (!currentUserIsAdmin()) {
    throw new Error('Only an admin can do that')
  }
}

function sessionEmployee(): StoredEmployee {
  const id = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SESSION_KEY) : null
  const employee = employees.find((item) => item.id === id && item.active)
  if (!employee) throw new Error('Enter your PIN to continue')
  return employee
}

function enrich(request: StoredRequest): LeaveRequest {
  const employee = employees.find((item) => item.id === request.employeeId)
  return {
    ...request,
    exceedsAccrual: employee
      ? annualRequestExceedsAccrual(employee, requests, request)
      : false,
  }
}

function assertNoOverlap(
  employee: StoredEmployee,
  draft: { startDate: string; endDate: string; startPortion: DayPortion; endPortion: DayPortion },
  ignoreId?: string,
): void {
  const clash = requests.find((request) => (
    request.employeeId === employee.id
    && request.id !== ignoreId
    && (request.status === 'approved' || request.status === 'pending')
    && leaveDaysOverlap(request, draft, employee.workingDays)
  ))
  if (clash) throw new Error('That overlaps leave already requested for this employee')
}

function portion(value: unknown, fallback: DayPortion): DayPortion {
  return value === 0.5 ? 0.5 : fallback
}

function parseLeaveType(value: unknown): LeaveType {
  if (value === 'sick' || value === 'unpaid' || value === 'study') return value
  return 'annual'
}

function hoursInPeriod(start: string, end: string): AdditionalHours[] {
  return additionalHours
    .filter((item) => item.date >= start && item.date <= end)
    .sort((a, b) => a.date.localeCompare(b.date) || a.employeeName.localeCompare(b.employeeName))
}

function assertPayPeriodOpen(date: string): void {
  const locked = reports.some((report) => report.status === 'sent' && date >= report.periodStart && date <= report.periodEnd)
  if (locked) throw new Error('That pay period has already been sent to the accountant')
}

function buildPendingRequest(employee: StoredEmployee, payload: Record<string, unknown>, enteredByAdmin: boolean): LeaveRequest {
  const type = parseLeaveType(payload.type)
  const startDate = String(payload.startDate || '')
  const endDate = String(payload.endDate || startDate)
  const startPortion = portion(payload.startPortion, 1)
  const endPortion = startDate === endDate ? startPortion : portion(payload.endPortion, 1)
  const draft = {
    id: newId('lv'),
    startDate,
    endDate,
    startPortion,
    endPortion,
    type,
  }
  if (expandLeaveDays(draft, employee.workingDays).length === 0) {
    throw new Error('This period includes no working days')
  }
  assertNoOverlap(employee, draft)
  if (type === 'sick') assertSickAllowed(employee, requests, draft, settings.statutorySickDays)
  if (type === 'study') assertStudyAllowed(employee, requests, draft)
  if (type === 'annual') assertAnnualAllowed(employee, requests, draft)
  const request: StoredRequest = {
    ...draft,
    employeeId: employee.id,
    employeeName: employee.name,
    status: 'pending',
    note: typeof payload.note === 'string' && payload.note.trim() ? payload.note.trim() : undefined,
    enteredByAdmin: enteredByAdmin || undefined,
    createdAt: new Date().toISOString(),
  }
  requests.push(request)
  return enrich(request)
}

function notifyLeaveDecision(request: StoredRequest, asOf: string): void {
  const employee = employees.find((item) => item.id === request.employeeId)
  if (!employee || (request.status !== 'approved' && request.status !== 'declined')) return
  const balance = leaveBalance(employee, requests, asOf, settings.statutorySickDays)
  const email = buildDecisionEmail({
    requestId: request.id,
    name: employee.name,
    email: String(employee.email || '').trim(),
    decision: request.status,
    type: request.type,
    startDate: request.startDate,
    endDate: request.endDate,
    startPortion: request.startPortion,
    endPortion: request.endPortion,
    note: request.note,
    decisionNote: request.decisionNote,
    asOf,
    annualAccrued: balance.annualAccrued,
    annualTaken: balance.annualTaken,
    annualBooked: balance.annualBooked,
    annualAvailable: balance.annualAvailable,
    sickEligible: balance.sickEligible,
    sickEligibleFrom: balance.sickEligibleFrom,
    sickAvailable: balance.sickAvailable,
    sickEntitlement: balance.sickEntitlement,
    studyEntitlement: balance.studyEntitlement,
    studyAvailable: balance.studyAvailable,
    studyYearStart: balance.studyYearStart,
    studyYearEnd: balance.studyYearEnd,
    createdAt: new Date().toISOString(),
  })
  if (email) emails.push(email)
}

function notifyAdminsOfRequest(request: LeaveRequest, action: 'submitted' | 'cancelled'): void {
  const email = buildRequestNoticeEmail({
    requestId: request.id,
    action,
    name: request.employeeName,
    type: request.type,
    startDate: request.startDate,
    endDate: request.endDate,
    startPortion: request.startPortion,
    endPortion: request.endPortion,
    note: request.note,
    exceedsAccrual: request.exceedsAccrual,
    settings,
    createdAt: new Date().toISOString(),
  })
  if (email) emails.push(email)
}

function optionalBalance(value: unknown): number | null | undefined {
  if (value === undefined) return undefined
  if (value === null || value === '') return null
  const days = Number(value)
  if (!Number.isFinite(days) || days < 0 || days > 365) throw new Error('Leave balance must be between 0 and 365 days')
  return Math.round(days * 100) / 100
}

function applyBalanceOverride(employee: StoredEmployee, payload: Record<string, unknown>, today: string): void {
  const annual = optionalBalance(payload.annualBalance)
  const sick = optionalBalance(payload.sickBalance)
  if (annual === undefined && sick === undefined) return
  const current = leaveBalance(employee, requests, today, settings.statutorySickDays)
  const stamped = new Date().toISOString()
  const freeze = payload.annualAccrualFrozen === true
  if (annual === null) {
    delete employee.annualBalanceOverride
    delete employee.annualBalanceOverrideAt
    delete employee.annualBalanceOverrideAsOf
    delete employee.annualAccrualFrozen
  } else if (annual !== undefined && (freeze || Math.abs(annual - current.annualAvailable) > 0.05)) {
    employee.annualBalanceOverride = annual
    employee.annualBalanceOverrideAt = stamped
    employee.annualBalanceOverrideAsOf = today
    if (freeze) employee.annualAccrualFrozen = true
    else delete employee.annualAccrualFrozen
  } else if (annual !== undefined && payload.annualAccrualFrozen === false) {
    delete employee.annualAccrualFrozen
  }
  if (sick === null) {
    delete employee.sickBalanceOverride
    delete employee.sickBalanceOverrideAt
  } else if (sick !== undefined && Math.abs(sick - current.sickAvailable) > 0.05) {
    employee.sickBalanceOverride = sick
    employee.sickBalanceOverrideAt = stamped
  }
}

function buildCurrentReport(today: string): PayReport {
  const payday = upcomingPayday(today)
  const dueDate = reportDueDate(payday)
  const period = payPeriodForPayday(payday)
  const lines = buildPayReportLines(employees.map(publicEmployee), requests, period)
  const periodHours = hoursInPeriod(period.start, period.end)
  const existing = reports.find((item) => item.payday === payday)
  if (existing?.status === 'sent') {
    return {
      payday: existing.payday,
      dueDate: existing.dueDate,
      periodStart: existing.periodStart,
      periodEnd: existing.periodEnd,
      status: 'sent',
      lines: existing.lines,
      additionalHours: existing.additionalHours,
      canApprove: false,
    }
  }
  if (today < dueDate) {
    return {
      payday,
      dueDate,
      periodStart: period.start,
      periodEnd: period.end,
      status: 'scheduled',
      lines,
      additionalHours: periodHours,
      canApprove: true,
    }
  }
  let report = reports.find((item) => item.payday === payday)
  if (!report) {
    report = {
      payday,
      dueDate,
      periodStart: period.start,
      periodEnd: period.end,
      status: 'pending_approval',
      lines,
      additionalHours: periodHours,
      readyEmailSent: false,
    }
    reports.push(report)
  }
  if (report.status === 'pending_approval') {
    report.lines = lines
    report.additionalHours = periodHours
    if (!report.readyEmailSent) {
      emails.push(buildReportReadyEmail({
        payday,
        dueDate,
        periodStart: period.start,
        periodEnd: period.end,
        lines,
        additionalHours: periodHours,
        settings,
        createdAt: new Date().toISOString(),
      }))
      report.readyEmailSent = true
    }
  }
  return {
    payday: report.payday,
    dueDate: report.dueDate,
    periodStart: report.periodStart,
    periodEnd: report.periodEnd,
    status: report.status,
    lines: report.lines,
    additionalHours: report.additionalHours,
    canApprove: report.status === 'pending_approval',
  }
}

function pathOf(url: string): string {
  return url.split('?')[0]
}

function monthParam(url: string, config?: { params?: { month?: string } }): string {
  const fromConfig = config?.params?.month
  if (fromConfig && /^\d{4}-\d{2}$/.test(fromConfig)) return fromConfig
  const query = url.split('?')[1] || ''
  const month = new URLSearchParams(query).get('month')
  if (month && /^\d{4}-\d{2}$/.test(month)) return month
  return leaveToday().slice(0, 7)
}

export async function handleLeaveMock(
  method: string,
  url: string,
  data?: unknown,
  config?: { params?: { month?: string } },
): Promise<unknown> {
  assertEnabled()
  const path = pathOf(url)
  const payload = (data || {}) as Record<string, unknown>
  const today = leaveToday()

  if (method === 'GET' && path === '/leave/calendar') {
    const month = monthParam(url, config)
    const bounds = monthBounds(month)
    const active = employees.filter((employee) => employee.active)
    const entries = requests
      .filter((request) => request.status === 'approved')
      .flatMap((request) => expandLeaveDays(request, employees.find((employee) => employee.id === request.employeeId)?.workingDays)
        .filter((day) => day.date >= bounds.start && day.date <= bounds.end)
        .map((day) => ({
          employeeId: request.employeeId,
          date: day.date,
          type: request.type,
          portion: day.portion,
        })))
    const calendar: LeaveCalendar = {
      month,
      employees: byEmployeeName(active).map((employee) => ({ id: employee.id, name: employee.name })),
      entries,
    }
    return calendar
  }

  if (method === 'GET' && path === '/leave/employees') {
    assertAdmin()
    return byEmployeeName(employees).map((employee) => employeeWithBalance(employee, today))
  }

  if (method === 'POST' && path === '/leave/employees') {
    assertAdmin()
    const pin = String(payload.pin || '')
    assertPinAvailable(pin)
    const name = String(payload.name || '').trim()
    const email = employeeEmail(payload.email)
    if (!name) throw new Error('Name is required')
    const pattern = workingPatternFromInput(payload)
    const startDate = String(payload.startDate || '')
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) throw new Error('Start date is required')
    const employee = seedEmployee({
      id: newId('emp'),
      name,
      email,
      ...pattern,
      annualLeaveDays: 0,
      studyLeaveDays: studyLeaveDaysFrom(payload.studyLeaveDays),
      startDate,
      active: payload.active !== false,
      pin,
    })
    assignAnnualLeave(employee, payload, true)
    applyBalanceOverride(employee, payload, today)
    employees.push(employee)
    const welcome = buildWelcomeEmail({
      name: employee.name,
      email: employee.email,
      pin,
      createdAt: new Date().toISOString(),
    })
    if (welcome) emails.push(welcome)
    return publicEmployee(employee)
  }

  if (method === 'PUT' && path.startsWith('/leave/employees/')) {
    assertAdmin()
    const id = path.split('/')[3]
    const employee = employees.find((item) => item.id === id)
    if (!employee) throw new Error('Employee not found')
    if (typeof payload.name === 'string' && payload.name.trim()) employee.name = payload.name.trim()
    if (typeof payload.email === 'string') employee.email = employeeEmail(payload.email)
    if (payload.workingDays !== undefined || payload.hoursPerDay !== undefined || payload.regularWeeklyHours !== undefined || payload.dailyHours !== undefined) {
      const pattern = payload.dailyHours !== undefined
        ? workingPatternFromInput({ dailyHours: payload.dailyHours })
        : workingPatternFromInput({
          workingDays: payload.workingDays !== undefined ? payload.workingDays : employee.workingDays,
          hoursPerDay: payload.hoursPerDay !== undefined ? payload.hoursPerDay : employee.hoursPerDay,
          regularWeeklyHours: payload.regularWeeklyHours !== undefined ? payload.regularWeeklyHours : employee.regularWeeklyHours,
        })
      employee.workingDays = pattern.workingDays
      employee.dailyHours = pattern.dailyHours
      employee.regularWeeklyHours = pattern.regularWeeklyHours
      if (pattern.hoursPerDay === undefined) delete employee.hoursPerDay
      else employee.hoursPerDay = pattern.hoursPerDay
    }
    if (payload.studyLeaveDays !== undefined) employee.studyLeaveDays = studyLeaveDaysFrom(payload.studyLeaveDays)
    assignAnnualLeave(employee, payload, false)
    if (typeof payload.startDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(payload.startDate)) {
      employee.startDate = payload.startDate
    }
    if (typeof payload.active === 'boolean') employee.active = payload.active
    if (payload.pin !== undefined && String(payload.pin).trim() !== '') {
      const pin = String(payload.pin).trim()
      assertPinAvailable(pin, employee.id)
      employee.pinHash = hashPin(pin)
    }
    applyBalanceOverride(employee, payload, today)
    requests.forEach((request) => {
      if (request.employeeId === employee.id) request.employeeName = employee.name
    })
    return publicEmployee(employee)
  }

  if (method === 'POST' && /^\/leave\/employees\/[^/]+\/reset-pin$/.test(path)) {
    assertAdmin()
    const id = path.split('/')[3]
    const employee = employees.find((item) => item.id === id)
    if (!employee) throw new Error('Employee not found')
    const requested = String(payload.pin || '').trim()
    const pin = requested || generatePin()
    assertPinAvailable(pin, employee.id)
    employee.pinHash = hashPin(pin)
    return { pin }
  }

  if (method === 'POST' && path === '/leave/session') {
    const pin = String(payload.pin || '')
    const employee = employees.find((item) => item.active && item.pinHash === hashPin(pin))
    if (!employee) throw new Error('That PIN is not recognised')
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(SESSION_KEY, employee.id)
    return {
      token: employee.id,
      employeeId: employee.id,
      name: employee.name,
      email: employee.email,
      balance: leaveBalance(employee, requests, today, settings.statutorySickDays),
    }
  }

  if (method === 'DELETE' && path === '/leave/session') {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(SESSION_KEY)
    return { ok: true }
  }

  if (method === 'POST' && path === '/leave/me/pin') {
    const employee = sessionEmployee()
    const pin = String(payload.pin || '').trim()
    assertPinAvailable(pin, employee.id)
    employee.pinHash = hashPin(pin)
    return { ok: true }
  }

  if (method === 'GET' && path === '/leave/me') {
    const employee = sessionEmployee()
    return {
      employee: publicEmployee(employee),
      balance: leaveBalance(employee, requests, today, settings.statutorySickDays),
      requests: requests.filter((request) => request.employeeId === employee.id).map(enrich),
    }
  }

  if (method === 'POST' && path === '/leave/requests') {
    const request = buildPendingRequest(sessionEmployee(), payload, false)
    notifyAdminsOfRequest(request, 'submitted')
    return request
  }

  if (method === 'POST' && path === '/leave/requests/for-employee') {
    assertAdmin()
    const employee = employees.find((item) => item.id === payload.employeeId && item.active)
    if (!employee) throw new Error('Choose an active employee')
    return buildPendingRequest(employee, payload, true)
  }

  if (method === 'GET' && path === '/leave/requests') {
    assertAdmin()
    return requests.map(enrich).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }

  if (method === 'POST' && /^\/leave\/requests\/[^/]+\/(approve|decline)$/.test(path)) {
    assertAdmin()
    const [, , , id, action] = path.split('/')
    const request = requests.find((item) => item.id === id)
    if (!request) throw new Error('Leave request not found')
    if (request.status !== 'pending') throw new Error('This request has already been reviewed')
    if (action === 'decline') {
      const decisionNote = typeof payload.decisionNote === 'string' ? payload.decisionNote.trim() : ''
      request.decisionNote = decisionNote || undefined
      request.status = 'declined'
      notifyLeaveDecision(request, today)
      return enrich(request)
    }
    const employee = employees.find((item) => item.id === request.employeeId)
    if (!employee) throw new Error('Employee not found')
    assertNoOverlap(employee, request, request.id)
    if (request.type === 'sick') {
      assertSickAllowed(employee, requests, request, settings.statutorySickDays)
    } else if (request.type === 'study') {
      assertStudyAllowed(employee, requests, request)
    } else if (request.type === 'annual') {
      assertAnnualAllowed(employee, requests, request)
    }
    request.status = 'approved'
    notifyLeaveDecision(request, today)
    return enrich(request)
  }

  if (method === 'POST' && /^\/leave\/requests\/[^/]+\/cancel$/.test(path)) {
    const employee = sessionEmployee()
    const id = path.split('/')[3]
    const request = requests.find((item) => item.id === id)
    if (!request || request.employeeId !== employee.id) throw new Error('Leave request not found')
    if (!canCancelRequest(request, today)) {
      throw new Error(request.status === 'approved'
        ? 'Approved leave cannot be cancelled after it has started'
        : 'This request can no longer be cancelled')
    }
    request.status = 'cancelled'
    const cancelled = enrich(request)
    notifyAdminsOfRequest(cancelled, 'cancelled')
    return cancelled
  }

  if (method === 'POST' && path === '/leave/balance-email') {
    const employee = sessionEmployee()
    const balance = leaveBalance(employee, requests, today, settings.statutorySickDays)
    if (!String(employee.email || '').includes('@')) throw new Error('This employee has no email address')
    const email = buildBalanceEmail({
      name: employee.name,
      email: employee.email,
      asOf: today,
      annualAccrued: balance.annualAccrued,
      annualTaken: balance.annualTaken,
      annualBooked: balance.annualBooked,
      annualAvailable: balance.annualAvailable,
      sickAvailable: balance.sickAvailable,
      sickEntitlement: balance.sickEntitlement,
      studyAvailable: balance.studyAvailable,
      studyEntitlement: balance.studyEntitlement,
      createdAt: new Date().toISOString(),
    })
    emails.push(email)
    return { sentTo: employee.email }
  }

  if (method === 'GET' && path === '/leave/settings') {
    assertAdmin()
    return settings
  }

  if (method === 'PUT' && path === '/leave/settings') {
    assertAdmin()
    if (typeof payload.accountantEmail === 'string' && payload.accountantEmail.includes('@')) {
      settings.accountantEmail = payload.accountantEmail.trim()
    }
    if (Array.isArray(payload.adminEmails)) {
      settings.adminEmails = payload.adminEmails.map((email) => String(email).trim()).filter((email) => email.includes('@'))
    }
    if (payload.statutorySickDays !== undefined) {
      const days = Number(payload.statutorySickDays)
      if (!Number.isFinite(days) || days < 0) throw new Error('Statutory sick days cannot be negative')
      settings.statutorySickDays = days
    }
    return settings
  }

  if (method === 'GET' && path === '/leave/pay-reports/current') {
    assertAdmin()
    return buildCurrentReport(today)
  }

  if (method === 'POST' && path === '/leave/pay-reports/current/notify') {
    assertAdmin()
    const current = buildCurrentReport(today)
    const email = buildAdminReportEmail({
      payday: current.payday,
      periodStart: current.periodStart,
      periodEnd: current.periodEnd,
      lines: current.lines,
      additionalHours: current.additionalHours,
      settings,
      createdAt: new Date().toISOString(),
    })
    if (!email) throw new Error('Add at least one admin email before sending the report')
    emails.push(email)
    return current
  }

  if (method === 'POST' && path === '/leave/pay-reports/current/approve') {
    assertAdmin()
    if (!settings.accountantEmail.includes('@')) throw new Error('Add the accountant email before sending the report')
    const payday = upcomingPayday(today)
    const dueDate = reportDueDate(payday)
    const period = payPeriodForPayday(payday)
    const lines = buildPayReportLines(employees.map(publicEmployee), requests, period)
    const periodHours = hoursInPeriod(period.start, period.end)
    let report = reports.find((item) => item.payday === payday)
    if (report?.status === 'sent') throw new Error('This pay report is not ready for approval')
    if (!report) {
      report = {
        payday,
        dueDate,
        periodStart: period.start,
        periodEnd: period.end,
        status: 'pending_approval',
        lines,
        additionalHours: periodHours,
        readyEmailSent: false,
      }
      reports.push(report)
    }
    report.lines = lines
    report.additionalHours = periodHours
    const sent = approvalEmails({
      status: 'pending_approval',
      payday: report.payday,
      periodStart: report.periodStart,
      periodEnd: report.periodEnd,
      lines: report.lines,
      additionalHours: report.additionalHours,
      settings,
      createdAt: new Date().toISOString(),
    })
    emails.push(...sent)
    report.status = 'sent'
    return buildCurrentReport(today)
  }

  if (method === 'POST' && path === '/leave/additional-hours') {
    assertAdmin()
    const employee = employees.find((item) => item.id === payload.employeeId && item.active)
    if (!employee) throw new Error('Choose an active employee')
    const date = String(payload.date || '')
    const hours = Number(payload.hours)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Date is required')
    if (!Number.isFinite(hours) || hours <= 0 || hours > 24) throw new Error('Enter the hours worked, up to 24')
    assertPayPeriodOpen(date)
    const entry: AdditionalHours = {
      id: newId('hrs'),
      employeeId: employee.id,
      employeeName: employee.name,
      date,
      hours: Math.round(hours * 100) / 100,
      note: typeof payload.note === 'string' && payload.note.trim() ? payload.note.trim() : undefined,
      createdAt: new Date().toISOString(),
    }
    additionalHours.push(entry)
    return entry
  }

  if (method === 'DELETE' && /^\/leave\/additional-hours\/[^/]+$/.test(path)) {
    assertAdmin()
    const id = path.split('/')[3]
    const entry = additionalHours.find((item) => item.id === id)
    if (!entry) throw new Error('Additional hours not found')
    assertPayPeriodOpen(entry.date)
    additionalHours = additionalHours.filter((item) => item.id !== id)
    return { id }
  }

  if (method === 'GET' && path === '/leave/outbox') {
    assertAdmin()
    return [...emails].reverse()
  }

  throw new Error(`Not found: ${url}`)
}
